/**
 * InlineVideoPlayer (Plan 04-07 / FIN-01 / BUG-005).
 *
 * Mints a 5-min scoped vtoken (GET video-token, which also audits view_video) and plays
 * the candidate's Stage-2 video INLINE via `<video controls src=…?vtoken=…>`. The native
 * controls give seek/scrub against the backend 206 range responses. We NEVER pop a blob
 * URL in a new tab (the post-await tab-open is silently blocked on mobile, BUG-005) — the
 * media plays inline. On a play error (typically the 5-min vtoken expired) we re-mint + retry.
 */
import { useTranslation } from "react-i18next";

import { videoStreamUrl } from "./api";
import { useVideoTokenQuery } from "./hooks";

export function InlineVideoPlayer({
  candidateId,
  hasVideo,
  vtoken,
}: {
  candidateId: number;
  hasVideo: boolean;
  /**
   * Pre-minted vtoken (Plan 04-08 HM surface — HM is not an HR role and cannot call the
   * HR-gated video-token endpoint, so HMFinalistDetail carries the token directly). When
   * absent (HR surface), the player mints its own + re-mints on expiry.
   */
  vtoken?: string;
}) {
  const { t } = useTranslation("rubric");
  const mint = useVideoTokenQuery(candidateId, hasVideo && !vtoken);
  const token = vtoken ?? mint.data?.vtoken;

  if (!hasVideo) {
    return <p className="text-sm text-muted-foreground">{t("video.none")}</p>;
  }
  if (!token) {
    if (mint.isError) {
      return <p className="text-sm text-destructive">{t("video.error")}</p>;
    }
    return <p className="text-sm text-muted-foreground">{t("video.loading")}</p>;
  }

  return (
    <div className="aspect-video w-full overflow-hidden rounded-md bg-black">
      <video
        controls
        playsInline
        preload="metadata"
        src={videoStreamUrl(candidateId, token)}
        className="h-full w-full"
        data-testid="inline-video"
        onError={() => {
          // HR surface: 5-min vtoken likely expired → re-mint + retry (inline, no tab pop).
          // HM surface: the token is supplied, so reload the detail to refresh it instead.
          if (!vtoken) void mint.refetch();
        }}
      >
        {t("video.unsupported")}
      </video>
    </div>
  );
}
